/**
 * Präzisierungs-Assistent (Δ Ninox, PRD §6/F13) — nur für Reviewer.
 *
 * Ein-Klick-Vorschlag: schärft Ist/Soll/Akzeptanzkriterien mit der VOLLEN
 * Ninox-Logik der betroffenen Tabelle (Formeln/Trigger) und benennt die
 * beteiligte Logik + offene Rückfragen. „Übernehmen" schreibt Ist/Soll/
 * Akzeptanzkriterien über den bestehenden Reviewer-PATCH zurück. Darunter
 * ein Chat zum Nachhaken an denselben internen KI-Service.
 */

import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ScanSearch, Send, Sparkles } from "lucide-react"
import { toast } from "sonner"
import { Markdown } from "@/components/Markdown"
import { api, apiError } from "@/services/api"
import type { PraezisierungChatNachricht, PraezisierungVorschlag, Report } from "@/types"

const SEKTION = "rounded-xl border bg-card p-5 shadow-card"
const TITEL = "mb-2.5 text-[13px] font-bold uppercase tracking-wide text-foreground/70"

export function PraezisierungsAssistent({ report }: { report: Report }) {
  const queryClient = useQueryClient()
  const [vorschlag, setVorschlag] = useState<PraezisierungVorschlag | null>(null)
  const [verlauf, setVerlauf] = useState<PraezisierungChatNachricht[]>([])
  const [frage, setFrage] = useState("")

  const praezisieren = useMutation({
    mutationFn: () => api.reports.praezisieren(report.id),
    onSuccess: (v) => setVorschlag(v),
    onError: (e) => toast.error(apiError(e)),
  })

  const uebernehmen = useMutation({
    mutationFn: () =>
      api.reports.update(report.id, {
        istBeschreibung: vorschlag!.ist || null,
        sollBeschreibung: vorschlag!.soll || null,
        akzeptanzkriterien: vorschlag!.akzeptanzkriterien,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] })
      toast.success("Anforderung übernommen — bitte prüfen")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const chat = useMutation({
    mutationFn: (nachricht: string) => api.reports.praezisierenChat(report.id, nachricht, verlauf),
    onSuccess: (antwort, nachricht) =>
      setVerlauf((v) => [
        ...v,
        { rolle: "reviewer", text: nachricht },
        { rolle: "ki", text: antwort },
      ]),
    onError: (e) => toast.error(apiError(e)),
  })

  const fragen = () => {
    const text = frage.trim()
    if (!text || chat.isPending) return
    setFrage("")
    chat.mutate(text)
  }

  return (
    <section className={SEKTION}>
      <div className="mb-2.5 flex items-center justify-between">
        <h2 className={`${TITEL} mb-0`}>Präzisierung mit Ninox-Logik</h2>
        <button
          type="button"
          onClick={() => praezisieren.mutate()}
          disabled={praezisieren.isPending}
          className="flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-[13px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          <ScanSearch className="h-3.5 w-3.5" />
          {praezisieren.isPending ? "Analysiere Ninox-Logik …" : "Mit Ninox-Logik präzisieren"}
        </button>
      </div>

      {!vorschlag && !praezisieren.isPending && (
        <p className="text-sm text-muted-foreground">
          Der Assistent liest die Formeln und Trigger der betroffenen Tabelle (read-only)
          und schlägt eine geschärfte Anforderung samt beteiligter Logik vor.
        </p>
      )}

      {vorschlag && (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-[10px] border bg-muted/30 px-3.5 py-3">
              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                Ist (Vorschlag)
              </p>
              <p className="whitespace-pre-wrap text-sm">{vorschlag.ist || "—"}</p>
            </div>
            <div className="rounded-[10px] border bg-muted/30 px-3.5 py-3">
              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                Soll (Vorschlag)
              </p>
              <p className="whitespace-pre-wrap text-sm">{vorschlag.soll || "—"}</p>
            </div>
          </div>

          {vorschlag.akzeptanzkriterien.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                Akzeptanzkriterien
              </p>
              <ul className="list-disc pl-5 text-sm leading-6">
                {vorschlag.akzeptanzkriterien.map((k, i) => (
                  <li key={i}>{k}</li>
                ))}
              </ul>
            </div>
          )}

          {vorschlag.beteiligteLogik && (
            <div className="rounded-[10px] border border-primary/15 bg-primary/5 px-3.5 py-3 text-sm">
              <p className="mb-1 flex items-center gap-1.5 font-bold text-primary">
                <Sparkles className="h-3.5 w-3.5" /> Beteiligte Logik
              </p>
              <div className="text-foreground/90">
                <Markdown inhalt={vorschlag.beteiligteLogik} />
              </div>
            </div>
          )}

          {vorschlag.rueckfragen.length > 0 && (
            <div className="rounded-[10px] border border-warning/30 bg-warning/10 px-3.5 py-3 text-sm">
              <p className="mb-1 font-bold text-warning">Offene Rückfragen an den Kunden</p>
              <ul className="list-disc pl-5 leading-6">
                {vorschlag.rueckfragen.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            </div>
          )}

          <button
            type="button"
            onClick={() => uebernehmen.mutate()}
            disabled={uebernehmen.isPending}
            className="self-start rounded-lg bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80 disabled:opacity-50"
          >
            Ist/Soll/Kriterien in die Meldung übernehmen
          </button>
        </div>
      )}

      {/* Nachhaken (Chat, derselbe interne KI-Service) */}
      {(vorschlag || verlauf.length > 0) && (
        <div className="mt-4 border-t pt-3">
          <div className="mb-2 flex flex-col gap-2">
            {verlauf.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] rounded-[10px] px-3 py-2 text-sm ${
                  m.rolle === "reviewer" ? "self-end bg-primary/10" : "self-start border bg-muted/40"
                }`}
              >
                {m.rolle === "ki" ? <Markdown inhalt={m.text} /> : m.text}
              </div>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <textarea
              value={frage}
              onChange={(e) => setFrage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  fragen()
                }
              }}
              rows={1}
              placeholder="Rückfrage an den Assistenten … (Enter sendet)"
              className="min-h-[40px] flex-1 rounded-lg border px-3 py-2 text-[13px]"
            />
            <button
              type="button"
              onClick={fragen}
              disabled={!frage.trim() || chat.isPending}
              className="flex h-[40px] items-center gap-1.5 rounded-lg bg-primary px-3.5 text-[13px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
